import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type { Language } from '@src/types.ts/language';

export type LanguageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

export class LanguageStore {
  activeLanguages: Language[] = [];
  loadingStatus: LanguageLoadingStatus = 'idle';

  private pendingLoad: Promise<void> | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async loadAll(): Promise<void> {
    if (this.loadingStatus === 'loaded') return;
    if (this.pendingLoad) return this.pendingLoad;

    runInAction(() => {
      this.loadingStatus = 'loading';
    });

    const run = async (): Promise<void> => {
      try {
        const langs =
          await this.rootStore.datoClientStore.languageRepository.getAllActiveLanguages();
        runInAction(() => {
          this.activeLanguages = langs;
          this.loadingStatus = 'loaded';
        });
      } catch {
        runInAction(() => {
          this.loadingStatus = 'error';
        });
      } finally {
        this.pendingLoad = null;
      }
    };

    this.pendingLoad = run();
    return this.pendingLoad;
  }

  getByValue(value: string): Language | undefined {
    return this.activeLanguages.find(l => l.value === value);
  }

  getByBaseLanguage(baseLanguage: string): Language[] {
    return this.activeLanguages.filter(l => l.baseLanguage === baseLanguage);
  }
}
